import React, { useEffect, useMemo, useState } from 'react';
import {
  AlertCircle,
  ArrowUpDown,
  Ban,
  Building2,
  Check,
  ChevronDown,
  Clock,
  Database,
  FileSpreadsheet,
  Layers,
  Link2,
  Loader2,
  Mail,
  MailX,
  Package,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCheck,
  Users,
  UserX,
  X,
} from 'lucide-react';
import DataTablePagination from '../../../components/DataTablePagination.jsx';
import { supabase } from '../../../lib/supabaseClient';
import { formatSaoPauloDateTimeLabel } from '../../../lib/saoPauloDateTime.js';
import { invokeAdminManageUser } from '../../../lib/adminManageUserClient.js';
import { exportRowsToExcel } from '../../../lib/utils.js';
import {
  buildCoreBaseIndex,
  coreBaseStatusLabel,
  fetchCoreBaseCache,
  formatCoreCnpj,
  formatCoreCpf,
  formatCorePhone,
  resolveCoreBaseProductScope,
  resolveCorePersonStatus,
  syncCoreBaseSnapshot,
} from '../../../lib/coreBase.js';

const CoreMetricCard = ({ label, value, icon: Icon, accentClass = 'text-teal-400' }) => (
  <div className="rounded-lg border border-slate-800 bg-slate-900/80 px-3 py-2.5 shadow-md shadow-slate-950/10">
    <div className="flex items-center justify-between gap-2">
      <span className="text-[9px] font-bold uppercase tracking-[0.16em] text-slate-500">{label}</span>
      {Icon ? <Icon size={14} className="text-slate-600" /> : null}
    </div>
    <div className={`mt-1 text-2xl font-black leading-none ${accentClass}`}>{value}</div>
  </div>
);

const STATUS_TONES = {
  Ativo: 'bg-teal-500/10 text-teal-300 border-teal-500/30',
  Inativo: 'bg-slate-700/40 text-slate-300 border-slate-600',
  Suspenso: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  Pendente: 'bg-amber-500/10 text-amber-300 border-amber-500/30',
  Aceito: 'bg-sky-500/10 text-sky-300 border-sky-500/30',
  Expirado: 'bg-orange-500/10 text-orange-300 border-orange-500/30',
  Cancelado: 'bg-slate-700/40 text-slate-400 border-slate-600',
  Recusado: 'bg-rose-500/10 text-rose-300 border-rose-500/30',
  'Sem convite': 'bg-slate-800/60 text-slate-400 border-slate-700',
};

const CoreStatusBadge = ({ status }) => {
  const label = coreBaseStatusLabel(status);
  const tone = STATUS_TONES[label] || (label === '—'
    ? 'bg-slate-800/60 text-slate-400 border-slate-700'
    : 'bg-slate-700/40 text-slate-300 border-slate-600');
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ${tone}`}>
      {label}
    </span>
  );
};

const STATUS_ICONS = {
  Ativo: UserCheck,
  Inativo: UserX,
  Suspenso: Ban,
  Pendente: Clock,
  Aceito: ShieldCheck,
  Expirado: Clock,
  Cancelado: Ban,
  Recusado: UserX,
  'Sem convite': MailX,
};

const PersonStatusBadge = ({ statusInfo, onResend, resending }) => {
  const { label = '—', source = '', canResend = false, action, tooltip } = statusInfo || {};
  const tone = STATUS_TONES[label] || 'bg-slate-800/60 text-slate-400 border-slate-700';
  const Icon = STATUS_ICONS[label] || AlertCircle;
  const actionLabel = action === 'create' ? 'Enviar' : 'Reenviar';
  const title = [
    label === '—' ? 'Status não informado' : label,
    tooltip || source,
  ].filter(Boolean).join(' · ');
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        title={title}
        aria-label={title}
        role="img"
        className={`inline-flex h-6 w-6 items-center justify-center rounded-full border ${tone}`}
      >
        <Icon size={13} aria-hidden="true" />
      </span>
      {canResend ? (
        <button
          type="button"
          onClick={onResend}
          disabled={resending}
          title={`${actionLabel} convite por e-mail`}
          aria-label={`${actionLabel} convite por e-mail`}
          className="inline-flex h-6 w-6 items-center justify-center rounded-full border border-teal-500/30 bg-teal-500/10 text-teal-300 transition hover:bg-teal-500/20 hover:text-teal-200 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {resending ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
        </button>
      ) : null}
    </span>
  );
};

const CoreFilterSelect = ({ label, options, value, onChange, allLabel = 'Todos' }) => {
  const [open, setOpen] = useState(false);
  const [term, setTerm] = useState('');
  const selected = new Set((value || []).map(String));
  const display = options.filter((option) => selected.has(String(option.id)));
  const normalizedTerm = term.trim().toLowerCase();
  const visibleOptions = (normalizedTerm
    ? options.filter((option) => String(option.name).toLowerCase().includes(normalizedTerm))
    : options
  )
    .slice()
    .sort((a, b) => {
      const aSelected = selected.has(String(a.id));
      const bSelected = selected.has(String(b.id));
      if (aSelected === bSelected) return 0;
      return aSelected ? -1 : 1;
    });
  const close = () => {
    setOpen(false);
    setTerm('');
  };
  const toggleOption = (id) => {
    const next = new Set(selected);
    if (next.has(String(id))) next.delete(String(id));
    else next.add(String(id));
    onChange(Array.from(next));
  };
  return (
    <div className="relative flex flex-col gap-2">
      <span className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500">{label}</span>
      <button
        type="button"
        onClick={() => (open ? close() : setOpen(true))}
        aria-expanded={open}
        className={`flex min-h-[48px] w-full items-center gap-2 rounded-lg border bg-slate-950 px-4 text-left text-sm text-white outline-none transition hover:border-slate-700 ${open ? 'border-teal-500' : 'border-slate-800'}`}
      >
        <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
          {display.length === 0 ? (
            <span className="text-slate-500">{allLabel}</span>
          ) : (
            display.map((option) => (
              <span key={option.id} className="inline-flex items-center gap-1 rounded border border-slate-700 bg-slate-800 px-2 py-0.5 text-[11px] text-slate-200">
                <span className="max-w-[140px] truncate">{option.name}</span>
                <X
                  size={12}
                  className="cursor-pointer text-slate-400 hover:text-white"
                  onClick={(event) => {
                    event.stopPropagation();
                    onChange((value || []).filter((item) => String(item) !== String(option.id)));
                  }}
                />
              </span>
            ))
          )}
        </span>
        {display.length > 0 ? (
          <span
            onClick={(event) => { event.stopPropagation(); onChange([]); }}
            className="cursor-pointer px-1 text-sm font-bold text-slate-400 hover:text-white"
            title="Limpar seleção"
          >
            ×
          </span>
        ) : null}
        <ChevronDown size={16} className={`shrink-0 text-slate-500 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open ? (
        <>
          <div className="fixed inset-0 z-40 cursor-default" onClick={close} aria-hidden="true" />
          <div className="absolute top-full z-50 mt-2 w-full overflow-hidden rounded-xl border border-slate-700 bg-slate-950 shadow-2xl ring-1 ring-black/50">
            <div className="flex items-center gap-2 border-b border-slate-800 px-3 py-2">
              <Search size={14} className="shrink-0 text-slate-500" />
              <input
                autoFocus
                value={term}
                onChange={(event) => setTerm(event.target.value)}
                onKeyDown={(event) => { if (event.key === 'Escape') close(); }}
                placeholder={`Digite para filtrar ${label.toLowerCase()}...`}
                aria-label={`Filtrar ${label}`}
                className="w-full min-w-0 bg-transparent text-sm text-white outline-none placeholder:text-slate-600"
              />
              {term ? (
                <button
                  type="button"
                  onClick={() => setTerm('')}
                  className="shrink-0 text-slate-500 transition hover:text-white"
                  title="Limpar digitação"
                >
                  <X size={13} />
                </button>
              ) : null}
            </div>
            <div className="max-h-64 overflow-auto p-1">
              {visibleOptions.length === 0 ? (
                <div className="p-3 text-center text-xs text-slate-500">Nenhuma opção encontrada</div>
              ) : (
                visibleOptions.map((option) => {
                  const isSelected = selected.has(String(option.id));
                  return (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => toggleOption(option.id)}
                      className={`flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm transition ${isSelected ? 'bg-teal-500/10 text-teal-200' : 'text-slate-300 hover:bg-slate-800 hover:text-white'}`}
                    >
                      <span className="truncate">{option.name}</span>
                      {isSelected ? <Check size={14} className="shrink-0 text-teal-400" /> : null}
                    </button>
                  );
                })
              )}
            </div>
            {display.length > 0 ? (
              <div className="flex items-center justify-between border-t border-slate-800 px-3 py-2">
                <span className="text-[11px] font-bold uppercase tracking-wide text-slate-500">
                  {display.length} selecionado{display.length > 1 ? 's' : ''}
                </span>
                <button
                  type="button"
                  onClick={() => onChange([])}
                  className="text-[11px] font-bold uppercase tracking-wide text-teal-400 transition hover:text-teal-300"
                >
                  Limpar
                </button>
              </div>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
};

const CoreEmptyState = ({ onSync, syncing }) => (
  <div className="rounded-xl border border-dashed border-slate-800 bg-slate-900/50 px-6 py-14 text-center">
    <Database size={36} className="mx-auto text-slate-600" />
    <h3 className="mt-4 text-base font-bold text-white">Nenhuma leitura da Base Única registrada</h3>
    <p className="mx-auto mt-2 max-w-xl text-sm text-slate-500">
      A SOFERA consome a Base Única (A2Care Core) em modo somente leitura. Execute a primeira
      sincronização para carregar empresas, pessoas, vínculos, áreas, cargos e produtos.
    </p>
    <button
      type="button"
      onClick={onSync}
      disabled={syncing}
      className="mt-6 inline-flex items-center gap-2 rounded-lg bg-teal-500 px-5 py-3 text-sm font-bold uppercase tracking-wide text-slate-950 transition hover:bg-teal-400 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
      {syncing ? 'Sincronizando...' : 'Sincronizar agora'}
    </button>
  </div>
);

const getDisplayValue = (row, column) => {
  if (column.kind === 'product-link') {
    return row.productLinks?.has(column.productId) ? (column.label || '') : '';
  }
  if (column.kind === 'company-areas') {
    return `Áreas (${row.areas_count || 0})`;
  }
  if (column.kind === 'company-positions') {
    return `Cargos (${row.positions_count || 0})`;
  }
  if (column.kind === 'person-status') {
    return row.statusInfo ? `${row.statusInfo.label} ${row.statusInfo.source || ''}`.trim() : '';
  }
  const raw = row[column.key];
  if (column.kind === 'status') return coreBaseStatusLabel(raw);
  if (column.formatter) return column.formatter(raw);
  if (raw === null || raw === undefined || raw === '') return '—';
  return String(raw);
};

const getFilterValues = (row, key) => {
  if (key === 'statusLabel') {
    const label = row.statusInfo?.label || '';
    return label && label !== '—' ? [label] : [];
  }
  const raw = key === 'company_name' ? (row.company_names || row[key]) : row[key];
  if (raw === null || raw === undefined) return [];
  const values = Array.isArray(raw)
    ? raw.map(String)
    : key === 'positions' ? String(raw).split(',') : [String(raw)];
  return values
    .map((value) => value.trim())
    .filter((value) => value && value !== '—');
};

const buildCoreBaseViews = () => {
  const resolvePerson = (ctx, id) => ctx.index.personById.get(String(id))?.full_name || '—';
  const resolveCompany = (ctx, id) => {
    const company = ctx.index.companyById.get(String(id));
    return company?.trade_name || company?.legal_name || '—';
  };

  return {
    companies: {
      label: 'Empresas',
      icon: Building2,
      columns: [
        { key: 'legal_name', label: 'Razão Social' },
        { key: 'trade_name', label: 'Nome Fantasia' },
        { key: 'cnpj', label: 'CNPJ', kind: 'mono', formatter: formatCoreCnpj },
        { key: 'email', label: 'E-mail' },
        { key: 'phone', label: 'Telefone', formatter: formatCorePhone },
        { key: 'areas', label: 'Áreas', kind: 'company-areas' },
        { key: 'positions', label: 'Cargos', kind: 'company-positions' },
        { key: 'status', label: 'Status', kind: 'status' },
      ],
      build: (ctx, onlyScoped) => ctx.index.companies
        .filter((row) => !onlyScoped || ctx.companyIds.has(String(row.id)))
        .map((row) => {
          const companyId = String(row.id);
          return {
            id: companyId,
            legal_name: row.legal_name || '—',
            trade_name: row.trade_name || '—',
            cnpj: row.cnpj || '',
            email: row.email || '',
            phone: row.phone || '',
            status: row.status,
            company_name: row.trade_name || row.legal_name || '—',
            areas_count: (ctx.index.areasByCompany?.get(companyId) || []).length,
            positions_count: (ctx.index.positionsByCompany?.get(companyId) || []).length,
          };
        }),
    },
    people: {
      label: 'Pessoas',
      icon: Users,
      columns: [
        { key: 'company_name', label: 'Empresa', sortable: true, truncate: true },
        { key: 'full_name', label: 'Nome', sortable: true },
        { key: 'email', label: 'E-mail', sortable: true },
        { key: 'cpf', label: 'CPF', kind: 'mono', formatter: formatCoreCpf },
        { key: 'phone', label: 'Telefone', formatter: formatCorePhone },
        { key: 'area', label: 'Área', sortable: true },
        { key: 'positions', label: 'Cargos', sortable: true, truncate: true },
        { key: 'sofera_role', label: 'Perfil', sortable: true, kind: 'profile-select', headClass: 'w-[140px]' },
        { key: 'status', label: 'Status', kind: 'person-status', headClass: 'w-[96px] text-center', cellClass: 'text-center' },
      ],
      build: (ctx, onlyScoped) => ctx.index.people
        .filter((row) => !onlyScoped || ctx.personIds.has(String(row.id)))
        .map((row) => {
          const personId = String(row.id);
          const positions = Array.from(ctx.positionsByPerson?.get(personId) || []).sort();
          const cpfDigits = String(row.cpf || '').replace(/\D/g, '');
          const emailNorm = String(row.email || '').trim().toLowerCase();
          const localProfile = (ctx.profilesByCentralId?.get(personId))
            || (cpfDigits && ctx.profilesByCpf?.get(cpfDigits))
            || (emailNorm && ctx.profilesByEmail?.get(emailNorm)) || null;
          // Todo usuário vindo do Core nasce como Aluno; sem conta local ainda,
          // exibimos o perfil padrão que será aplicado na ativação.
          const soferaRole = localProfile?.role || 'Aluno';
          const soferaPendingInvite = (emailNorm && ctx.soferaInvitesByEmail?.get(emailNorm)) || null;
          const coreInvitations = (personId && ctx.invitationsByPersonId?.get(personId))
            || (emailNorm && ctx.invitationsByEmail?.get(emailNorm)) || [];
          const authLinks = (personId && ctx.authLinksByPersonId?.get(personId)) || [];
          const coreCompanyIds = ctx.coreCompanyIdsByPerson?.get(personId) || [];
          const scopedCompanyIds = coreCompanyIds.filter((cid) => ctx.companyIds?.has(cid));
          // Empresa exibida: prioriza as empresas do produto quando há escopo.
          const displayCompanyIds = scopedCompanyIds.length ? scopedCompanyIds : coreCompanyIds;
          const companyNames = Array.from(new Set(displayCompanyIds
            .map((cid) => ctx.index.companyById?.get(String(cid)))
            .filter(Boolean)
            .map((company) => company.trade_name || company.legal_name)
            .filter(Boolean)));
          const localCompanyId = (scopedCompanyIds.length ? scopedCompanyIds : coreCompanyIds)
            .map((cid) => ctx.localCompanyByCentralId?.get(cid))
            .find(Boolean) || '';
          const statusInfo = resolveCorePersonStatus({
            coreStatus: row.status,
            coreInvitations,
            authLinks,
            soferaPendingInvite,
          });
          return {
            id: personId,
            company_name: companyNames.join(', ') || '—',
            company_names: companyNames,
            full_name: row.full_name || '—',
            email: row.email || '',
            cpf: row.cpf || '',
            phone: row.phone || '',
            area: row.area || '',
            positions: positions.join(', '),
            sofera_role: soferaRole,
            profile_id: localProfile?.id || '',
            has_profile: Boolean(localProfile?.id),
            sas_partner_id: localProfile?.sas_partner_id || '',
            status: row.status,
            statusInfo,
            local_company_id: localCompanyId,
          };
        }),
    },
    links: {
      label: 'Vínculos',
      icon: Link2,
      columns: [
        { key: 'person_name', label: 'Pessoa' },
        { key: 'company_name', label: 'Empresa' },
        { key: 'role', label: 'Papel' },
        { key: 'area_name', label: 'Área' },
        { key: 'position_name', label: 'Cargo' },
        { key: 'is_primary', label: 'Principal' },
        { key: 'status', label: 'Status', kind: 'status' },
      ],
      build: (ctx, onlyScoped) => ctx.index.company_users
        .filter((row) => !onlyScoped
          || (ctx.personIds.has(String(row.user_id)) && ctx.companyIds.has(String(row.company_id))))
        .map((row) => ({
          id: String(row.id),
          person_name: resolvePerson(ctx, row.user_id),
          company_name: resolveCompany(ctx, row.company_id),
          role: row.role || '',
          area_name: row.area_id ? (ctx.index.areaById.get(String(row.area_id))?.name || '—') : '—',
          position_name: row.position_id ? (ctx.index.positionById.get(String(row.position_id))?.name || '—') : '—',
          is_primary: row.is_primary === true ? 'Sim' : 'Não',
          status: row.status,
        })),
    },
    areas: {
      label: 'Áreas',
      icon: Layers,
      columns: [
        { key: 'name', label: 'Área' },
        { key: 'company_name', label: 'Empresa' },
        { key: 'status', label: 'Status', kind: 'status' },
      ],
      build: (ctx, onlyScoped) => ctx.index.company_areas
        .filter((row) => !onlyScoped || ctx.companyIds.has(String(row.company_id)))
        .map((row) => ({
          id: String(row.id),
          name: row.name || '—',
          company_name: resolveCompany(ctx, row.company_id),
          status: row.status,
        })),
    },
    positions: {
      label: 'Cargos',
      icon: ShieldCheck,
      columns: [
        { key: 'name', label: 'Cargo' },
        { key: 'company_name', label: 'Empresa' },
        { key: 'status', label: 'Status', kind: 'status' },
      ],
      build: (ctx, onlyScoped) => ctx.index.company_positions
        .filter((row) => !onlyScoped || ctx.companyIds.has(String(row.company_id)))
        .map((row) => ({
          id: String(row.id),
          name: row.name || '—',
          company_name: resolveCompany(ctx, row.company_id),
          status: row.status,
        })),
    },
    products: {
      label: 'Produtos',
      icon: Package,
      columns: [
        { key: 'code', label: 'Código', kind: 'mono' },
        { key: 'name', label: 'Produto' },
        { key: 'is_visible', label: 'Visível' },
        { key: 'status', label: 'Status', kind: 'status' },
      ],
      build: (ctx) => ctx.index.products.map((row) => ({
        id: String(row.id),
        code: row.code || '—',
        name: row.name || '—',
        is_visible: row.is_visible === false ? 'Não' : 'Sim',
        status: row.status,
      })),
    },
    productLinks: {
      label: 'Produtos Vinculados',
      icon: Package,
      columns: [
        { key: 'owner_name', label: 'Empresa' },
      ],
      getColumns(ctx) {
        const linkedProductIds = new Set();
        ctx.index.company_products.forEach((row) => linkedProductIds.add(String(row.product_id)));
        const products = ctx.index.products
          .filter((product) => linkedProductIds.has(String(product.id)))
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        return [
          { key: 'owner_name', label: 'Empresa' },
          ...products.map((product) => ({
            key: `product_${product.id}`,
            label: product.name || '—',
            kind: 'product-link',
            productId: String(product.id),
          })),
        ];
      },
      build: (ctx, onlyScoped) => {
        const owners = new Map();
        const ensure = (key, ownerName) => {
          if (!owners.has(key)) {
            owners.set(key, {
              id: key,
              owner_name: ownerName,
              productLinks: new Set(),
            });
          }
          return owners.get(key);
        };
        ctx.index.company_products
          .filter((row) => !onlyScoped || ctx.companyIds.has(String(row.company_id)))
          .forEach((row) => {
            const owner = ensure(`company:${row.company_id}`, resolveCompany(ctx, row.company_id));
            owner.productLinks.add(String(row.product_id));
          });
        return Array.from(owners.values()).sort((a, b) => a.owner_name.localeCompare(b.owner_name));
      },
    },
    globalRoles: {
      label: 'Papéis Globais',
      icon: ShieldCheck,
      columns: [
        { key: 'code', label: 'Código', kind: 'mono', sortable: true },
        { key: 'name', label: 'Papel', sortable: true },
        { key: 'description', label: 'Descrição', sortable: true },
        { key: 'status', label: 'Status', kind: 'status' },
      ],
      build: (ctx) => ctx.index.global_roles.map((row) => ({
        id: String(row.id),
        code: row.code || '—',
        name: row.name || '—',
        description: row.description || '—',
        status: row.status,
      })),
    },
  };
};

const ENTITY_VIEWS = buildCoreBaseViews();
const ENTITY_ORDER = ['companies', 'people', 'links', 'areas', 'positions', 'products', 'productLinks', 'globalRoles'];
const SCOPED_ENTITIES = new Set(['companies', 'people', 'links', 'areas', 'positions', 'productLinks']);
const FILTER_CONFIGS = {
  companies: [
    { key: 'company_name', label: 'Nome da Empresa', allLabel: 'Todas as Empresas' },
    { key: 'statusLabel', label: 'Status', allLabel: 'Todos os Status' },
  ],
  people: [
    { key: 'full_name', label: 'Nome', allLabel: 'Todos os Nomes' },
    { key: 'company_name', label: 'Empresa', allLabel: 'Todas as Empresas' },
    { key: 'area', label: 'Área', allLabel: 'Todas as Áreas' },
    { key: 'positions', label: 'Cargos', allLabel: 'Todos os Cargos' },
    { key: 'sofera_role', label: 'Perfil', allLabel: 'Todos os Perfis' },
    { key: 'statusLabel', label: 'Status', allLabel: 'Todos os Status' },
  ],
  links: [
    { key: 'role', label: 'Papel', allLabel: 'Todos os Papéis' },
    { key: 'area_name', label: 'Área', allLabel: 'Todas as Áreas' },
    { key: 'position_name', label: 'Cargo', allLabel: 'Todos os Cargos' },
    { key: 'is_primary', label: 'Principal', allLabel: 'Todos os Vínculos' },
    { key: 'statusLabel', label: 'Status', allLabel: 'Todos os Status' },
  ],
  areas: [
    { key: 'company_name', label: 'Empresa', allLabel: 'Todas as Empresas' },
    { key: 'statusLabel', label: 'Status', allLabel: 'Todos os Status' },
  ],
  positions: [
    { key: 'company_name', label: 'Empresa', allLabel: 'Todas as Empresas' },
    { key: 'statusLabel', label: 'Status', allLabel: 'Todos os Status' },
  ],
  products: [
    { key: 'is_visible', label: 'Visível', allLabel: 'Todos' },
    { key: 'statusLabel', label: 'Status', allLabel: 'Todos os Status' },
  ],
  globalRoles: [
    { key: 'name', label: 'Papel', allLabel: 'Todos os Papéis' },
    { key: 'statusLabel', label: 'Status', allLabel: 'Todos os Status' },
  ],
};

const BASE_ROLES = ['Aluno', 'Instrutor', 'Coordenador', 'Ponto Focal', 'Parceiro SAS', 'Gestor Parceiro', 'Admin'];
const PARTNER_ROLES = new Set(['Parceiro SAS', 'Gestor Parceiro']);

const sortRoles = (roles) => Array.from(new Set(roles))
  .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }));

const CoreBaseTab = ({ entities } = {}) => {
  const visibleEntities = entities && entities.length
    ? ENTITY_ORDER.filter((key) => entities.includes(key))
    : ENTITY_ORDER;
  const defaultEntity = visibleEntities[0] || 'companies';
  const [cache, setCache] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [activeEntity, setActiveEntity] = useState(defaultEntity);
  const [filters, setFilters] = useState({});
  const [sortConfig, setSortConfig] = useState(null);
  const [onlyScoped, setOnlyScoped] = useState(false);
  const [page, setPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [soferaProfilesByCpf, setSoferaProfilesByCpf] = useState(new Map());
  const [profilesByCpf, setProfilesByCpf] = useState(new Map());
  const [profilesByEmail, setProfilesByEmail] = useState(new Map());
  const [profilesByCentralId, setProfilesByCentralId] = useState(new Map());
  const [soferaInvitesByEmail, setSoferaInvitesByEmail] = useState(new Map());
  const [localCompanyByCentralId, setLocalCompanyByCentralId] = useState(new Map());
  const [resendState, setResendState] = useState({});
  const [detailModal, setDetailModal] = useState(null);
  const [profileEditModal, setProfileEditModal] = useState(null);
  const [profileEditSaving, setProfileEditSaving] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [soferaRoles, setSoferaRoles] = useState(() => sortRoles(BASE_ROLES));
  const [selectedPersonIds, setSelectedPersonIds] = useState([]);
  const [bulkRole, setBulkRole] = useState('');
  const [bulkApplying, setBulkApplying] = useState(false);
  const [savingRoleByPerson, setSavingRoleByPerson] = useState({});
  const [currentUserId, setCurrentUserId] = useState('');

  const loadCache = async () => {
    const row = await fetchCoreBaseCache();
    setCache(row);
    return row;
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const [profilesRes, invitesRes, companiesRes] = await Promise.allSettled([
        supabase.from('profiles').select('id, email, cpf, role, status, full_name, company_id, sas_partner_id, central_person_id, whatsapp'),
        supabase.from('sofera_invites').select('id, email, full_name, role, company_id, sas_partner_id, status, expires_at')
          .eq('status', 'pending'),
        supabase.from('companies').select('id, central_company_id'),
      ]);
      if (cancelled) return;

      // Cada carga é independente: uma falha em convites/empresas nunca pode
      // apagar a resolução de perfis (causa do "Sem perfil na SOFERA" em massa).
      const profiles = profilesRes.status === 'fulfilled' && !profilesRes.value.error
        ? (profilesRes.value.data || [])
        : null;
      const invites = invitesRes.status === 'fulfilled' && !invitesRes.value.error
        ? (invitesRes.value.data || [])
        : [];
      const localCompanies = companiesRes.status === 'fulfilled' && !companiesRes.value.error
        ? (companiesRes.value.data || [])
        : [];

      const roleByCpf = new Map();
      const byCpf = new Map();
      const byEmail = new Map();
      const byCentralId = new Map();
      for (const row of profiles || []) {
        const cpf = String(row.cpf || '').replace(/\D/g, '');
        const email = String(row.email || '').trim().toLowerCase();
        const centralId = String(row.central_person_id || '').trim();
        if (cpf) {
          roleByCpf.set(cpf, row);
          byCpf.set(cpf, row);
        }
        if (email) byEmail.set(email, row);
        if (centralId) byCentralId.set(centralId, row);
      }
      const invitesByEmail = new Map();
      for (const row of invites) {
        const email = String(row.email || '').trim().toLowerCase();
        if (email) invitesByEmail.set(email, row);
      }
      const companyByCentralId = new Map();
      for (const row of localCompanies) {
        if (row?.central_company_id) companyByCentralId.set(String(row.central_company_id), row.id);
      }
      if (cancelled) return;
      // Só substitui os perfis se a consulta de perfis funcionou.
      if (profiles) {
        setSoferaProfilesByCpf(roleByCpf);
        setProfilesByCpf(byCpf);
        setProfilesByEmail(byEmail);
        setProfilesByCentralId(byCentralId);
      }
      setSoferaInvitesByEmail(invitesByEmail);
      setLocalCompanyByCentralId(companyByCentralId);
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase.auth.getUser();
      if (!cancelled) setCurrentUserId(String(data?.user?.id || ''));
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const row = await loadCache();
        // Snapshot antigo (sem ativações/convites): ressincroniza automaticamente.
        if (!cancelled && row?.dataset && !Array.isArray(row.dataset.person_auth_links)) {
          await syncCoreBaseSnapshot();
          if (!cancelled) await loadCache();
        }
      } catch (err) {
        if (!cancelled) setError(String(err?.message || err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data, error: rolesErr } = await supabase
          .from('role_permissions')
          .select('role');
        if (rolesErr) throw rolesErr;
        const roles = (data || []).map((row) => String(row.role || '').trim()).filter(Boolean);
        if (!cancelled) setSoferaRoles(sortRoles([...BASE_ROLES, ...roles]));
      } catch (err) {
        if (!cancelled) setSoferaRoles(sortRoles(BASE_ROLES));
      }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    setFilters({});
    setSortConfig(null);
    setSelectedPersonIds([]);
    setBulkRole('');
  }, [activeEntity]);

  useEffect(() => { setPage(1); }, [activeEntity, filters, sortConfig, onlyScoped, itemsPerPage]);

  const handleSync = async () => {
    setSyncing(true);
    setError('');
    try {
      await syncCoreBaseSnapshot();
      await loadCache();
    } catch (err) {
      setError(String(err?.message || err));
    } finally {
      setSyncing(false);
    }
  };

  const scope = useMemo(() => {
    const base = resolveCoreBaseProductScope(cache?.dataset);
    const positionsByPerson = new Map();
    const coreCompanyIdsByPerson = new Map();
    for (const link of base.index.company_users) {
      const personId = String(link.user_id);
      if (link.company_id) {
        if (!coreCompanyIdsByPerson.has(personId)) coreCompanyIdsByPerson.set(personId, []);
        coreCompanyIdsByPerson.get(personId).push(String(link.company_id));
      }
      const positionId = link.position_id ? String(link.position_id) : null;
      if (!positionId) continue;
      const positionName = base.index.positionById.get(positionId)?.name;
      if (!positionName) continue;
      if (!positionsByPerson.has(personId)) positionsByPerson.set(personId, new Set());
      positionsByPerson.get(personId).add(positionName);
    }

    // Vínculos com o Acesso Único: única fonte real de "já ativou".
    const authLinksByPersonId = new Map();
    const personIdByAuthUserId = new Map();
    const authLinks = Array.isArray(cache?.dataset?.person_auth_links)
      ? cache.dataset.person_auth_links
      : [];
    for (const link of authLinks) {
      if (!link?.person_id) continue;
      const pid = String(link.person_id);
      if (!authLinksByPersonId.has(pid)) authLinksByPersonId.set(pid, []);
      authLinksByPersonId.get(pid).push(link);
      if (link.auth_user_id) personIdByAuthUserId.set(String(link.auth_user_id), pid);
    }

    // Convites do Core não têm person_id: indexa por e-mail e por central_user_id.
    const personIdByEmail = new Map();
    for (const person of base.index.people) {
      const email = String(person?.email || '').trim().toLowerCase();
      if (email) personIdByEmail.set(email, String(person.id));
    }
    const invitationsByPersonId = new Map();
    const invitationsByEmail = new Map();
    const invitations = Array.isArray(cache?.dataset?.invitations) ? cache.dataset.invitations : [];
    for (const inv of invitations) {
      const email = String(inv?.email || '').trim().toLowerCase();
      if (email) {
        if (!invitationsByEmail.has(email)) invitationsByEmail.set(email, []);
        invitationsByEmail.get(email).push(inv);
      }
      const pid = (inv?.person_id && String(inv.person_id))
        || (email && personIdByEmail.get(email))
        || (inv?.central_user_id && personIdByAuthUserId.get(String(inv.central_user_id)))
        || null;
      if (pid) {
        if (!invitationsByPersonId.has(pid)) invitationsByPersonId.set(pid, []);
        invitationsByPersonId.get(pid).push(inv);
      }
    }

    return {
      ...base,
      soferaProfilesByCpf,
      profilesByCpf,
      profilesByEmail,
      profilesByCentralId,
      soferaInvitesByEmail,
      positionsByPerson,
      coreCompanyIdsByPerson,
      localCompanyByCentralId,
      authLinksByPersonId,
      invitationsByPersonId,
      invitationsByEmail,
    };
  }, [cache, soferaProfilesByCpf, profilesByCpf, profilesByEmail, profilesByCentralId, soferaInvitesByEmail, localCompanyByCentralId]);

  const handleResendInvite = async (row) => {
    const email = String(row.email || row.statusInfo?.inviteEmail || '').trim().toLowerCase();
    if (!email) return;
    const isNewInvite = row.statusInfo?.action === 'create';
    // Preferência: dados do perfil local; fallback ao convite pendente; fallback ao snapshot.
    const cpfDigits = String(row.cpf || '').replace(/\D/g, '');
    // Telefone do Core (tela PESSOAS): preenche o WhatsApp do perfil e é usado
    // como destino das mensagens de WhatsApp do respectivo usuário.
    const whatsappDigits = String(row.phone || '').replace(/\D/g, '');
    const localProfile = (cpfDigits && profilesByCpf.get(cpfDigits))
      || (email && profilesByEmail.get(email)) || null;
    const pendingInvite = (email && soferaInvitesByEmail.get(email)) || null;

    if (isNewInvite && !localProfile) {
      const companyId = row.local_company_id || pendingInvite?.company_id || '';
      if (!companyId) {
        alert(`Não foi possível identificar a empresa de ${email} na SOFERA. Cadastre o vínculo e tente novamente.`);
        return;
      }
      setResendState((s) => ({ ...s, [row.id]: true }));
      try {
        const { error } = await invokeAdminManageUser({
          action: 'create',
          payload: {
            email,
            full_name: row.full_name && row.full_name !== '—' ? row.full_name : email,
            role: 'Aluno',
            status: 'Pendente',
            company_id: companyId,
            whatsapp: whatsappDigits || undefined,
            channels: { email: true, sms: false, whatsapp: false },
          },
        });
        if (error) throw new Error(error.message || 'Falha ao enviar convite.');
        setResendState((s) => ({ ...s, [row.id]: false }));
        alert(`Convite enviado para ${email}.`);
      } catch (err) {
        setResendState((s) => ({ ...s, [row.id]: false }));
        alert(`Erro ao enviar convite: ${err.message}`);
      }
      return;
    }

    setResendState((s) => ({ ...s, [row.id]: true }));
    try {
      const payload = {
        id: localProfile?.id || undefined,
        email,
        full_name: row.full_name && row.full_name !== '—' ? row.full_name : undefined,
        role: localProfile?.role || pendingInvite?.role || 'Aluno',
        status: localProfile?.status || 'Pendente',
        company_id: localProfile?.company_id || pendingInvite?.company_id || undefined,
        sas_partner_id: localProfile?.sas_partner_id || pendingInvite?.sas_partner_id || undefined,
        whatsapp: whatsappDigits || localProfile?.whatsapp || undefined,
        channels: { email: true, sms: false, whatsapp: true },
      };
      const { data, error } = await invokeAdminManageUser({ action: 'resend_invite', payload });
      if (error) throw new Error(error.message || 'Falha ao reenviar convite.');
      setResendState((s) => ({ ...s, [row.id]: false }));
      alert(`Convite reenviado para ${email}.`);
    } catch (err) {
      setResendState((s) => ({ ...s, [row.id]: false }));
      alert(`Erro ao reenviar convite: ${err.message}`);
    }
  };

  const openDetailModal = (type, row) => {
    const companyId = String(row.id);
    const items = type === 'areas'
      ? (scope.index.areasByCompany?.get(companyId) || [])
      : (scope.index.positionsByCompany?.get(companyId) || []);
    setDetailModal({
      type,
      companyName: row.company_name || row.trade_name || row.legal_name || 'Empresa',
      items: items.map((item) => ({ name: item.name || '—', status: item.status })),
    });
  };

  const view = ENTITY_VIEWS[activeEntity] || ENTITY_VIEWS.companies;
  const scopedActive = onlyScoped && SCOPED_ENTITIES.has(activeEntity);

  const columns = useMemo(
    () => (view.getColumns ? view.getColumns(scope) : view.columns),
    [view, scope],
  );

  const rows = useMemo(
    () => (cache ? view.build(scope, scopedActive) : []),
    [cache, view, scope, scopedActive],
  );

  const filterConfigs = useMemo(() => FILTER_CONFIGS[activeEntity] || [], [activeEntity]);

  const filterOptions = useMemo(() => {
    const result = {};
    filterConfigs.forEach((filter) => {
      if (activeEntity === 'people' && filter.key === 'sofera_role') {
        result[filter.key] = soferaRoles.map((role) => ({ id: role, name: role }));
        return;
      }
      const values = new Set();
      rows.forEach((row) => {
        getFilterValues(row, filter.key).forEach((value) => values.add(value));
      });
      result[filter.key] = Array.from(values)
        .sort((a, b) => a.localeCompare(b, 'pt-BR', { sensitivity: 'base' }))
        .map((name) => ({ id: name, name }));
    });
    return result;
  }, [rows, filterConfigs, activeEntity, soferaRoles]);

  const hasActiveFilters = Object.values(filters).some((values) => values?.length > 0);

  const clearFilters = () => {
    setFilters({});
  };

  const toggleSort = (columnKey) => {
    setSortConfig((current) => (
      current?.columnKey === columnKey
        ? { columnKey, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { columnKey, direction: 'asc' }
    ));
  };

  const filteredRows = useMemo(() => {
    const activeFilters = filterConfigs
      .map((filter) => ({ key: filter.key, values: filters[filter.key] || [] }))
      .filter((filter) => filter.values.length > 0);
    return rows.filter((row) => !activeFilters.some((filter) => (
      !getFilterValues(row, filter.key).some((value) => filter.values.includes(value))
    )));
  }, [rows, filters, filterConfigs]);

  const sortedRows = useMemo(() => {
    const column = sortConfig ? columns.find((item) => item.key === sortConfig.columnKey) : null;
    if (!column) return filteredRows;
    const direction = sortConfig.direction === 'desc' ? -1 : 1;
    return [...filteredRows].sort((a, b) => {
      const aValue = column.sortValue ? column.sortValue(a) : getDisplayValue(a, column);
      const bValue = column.sortValue ? column.sortValue(b) : getDisplayValue(b, column);
      return String(aValue).localeCompare(String(bValue), 'pt-BR', { sensitivity: 'base' }) * direction;
    });
  }, [filteredRows, sortConfig, columns]);

  const pagedRows = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return sortedRows.slice(start, start + itemsPerPage);
  }, [sortedRows, page, itemsPerPage]);

  // Exporta exatamente o conjunto filtrado/ordenado exibido na tela.
  const handleExportExcel = async () => {
    if (!sortedRows.length) return;
    setExporting(true);
    try {
      const stamp = new Date().toISOString().split('T')[0];
      await exportRowsToExcel({
        fileName: `${activeEntity}-${stamp}.xlsx`,
        sheetName: view.label,
        headers: columns.map((column) => column.label),
        rows: sortedRows.map((row) => columns.map((column) => getDisplayValue(row, column))),
      });
    } catch (err) {
      console.error('Falha ao exportar Excel', err);
      alert('Não foi possível exportar para Excel. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  const roleOptionsFor = (row) => sortRoles(
    [...soferaRoles, String(row.sofera_role || '').trim()].filter(Boolean),
  );

  // O próprio usuário logado não pode alterar o próprio perfil.
  const isOwnProfileRow = (row) => Boolean(currentUserId)
    && String(row?.profile_id || '') === String(currentUserId);

  const patchLocalProfile = (profileId, patch) => {
    const apply = (prev) => {
      let changed = false;
      const next = new Map(prev);
      for (const [key, value] of next) {
        if (value?.id === profileId) {
          next.set(key, { ...value, ...patch });
          changed = true;
        }
      }
      return changed ? next : prev;
    };
    setSoferaProfilesByCpf(apply);
    setProfilesByCpf(apply);
    setProfilesByEmail(apply);
    setProfilesByCentralId(apply);
  };

  const persistProfileRole = async (profileIds, role) => {
    const ids = Array.from(new Set(profileIds.filter(Boolean)));
    if (!ids.length) throw new Error('Nenhum perfil local encontrado.');
    const { data, error } = await supabase
      .from('profiles')
      .update({ role })
      .in('id', ids)
      .select('id, role');
    if (error) throw new Error(error.message);
    if ((data || []).length !== ids.length) {
      throw new Error('Sem permissão para alterar o perfil de um ou mais usuários.');
    }
  };

  const validateRoleAssignment = (targets, role) => {
    if (!PARTNER_ROLES.has(role)) return true;
    const withoutPartner = targets.filter((target) => !target.sas_partner_id);
    if (withoutPartner.length) {
      alert(`O perfil "${role}" exige um Parceiro SAS vinculado. ${withoutPartner.length} usuário(s) selecionado(s) não possuem esse vínculo.`);
      return false;
    }
    return true;
  };

  const handleRoleChange = async (row, nextRole) => {
    const profileId = String(row.profile_id || '');
    if (!profileId || nextRole === row.sofera_role) return;
    if (isOwnProfileRow(row)) {
      alert('Você não pode alterar o seu próprio perfil.');
      return;
    }
    if (!validateRoleAssignment([row], nextRole)) return;
    setSavingRoleByPerson((current) => ({ ...current, [row.id]: true }));
    try {
      await persistProfileRole([profileId], nextRole);
      patchLocalProfile(profileId, { role: nextRole });
    } catch (err) {
      alert(`Erro ao alterar perfil: ${err.message}`);
    } finally {
      setSavingRoleByPerson((current) => ({ ...current, [row.id]: false }));
    }
  };

  const selectablePersonIds = useMemo(
    () => filteredRows
      .filter((row) => row.has_profile && !isOwnProfileRow(row))
      .map((row) => String(row.id)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filteredRows, currentUserId],
  );
  const selectedPersonIdSet = useMemo(
    () => new Set(selectedPersonIds.map(String)),
    [selectedPersonIds],
  );
  const allSelectableSelected = selectablePersonIds.length > 0
    && selectablePersonIds.every((id) => selectedPersonIdSet.has(id));
  const someSelectableSelected = selectedPersonIds.length > 0 && !allSelectableSelected;
  const selectedPeople = useMemo(
    () => rows.filter((row) => row.has_profile
      && !isOwnProfileRow(row)
      && selectedPersonIdSet.has(String(row.id))),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, selectedPersonIdSet, currentUserId],
  );

  const toggleSelectAllPeople = () => {
    if (allSelectableSelected) {
      setSelectedPersonIds((prev) => prev.filter((id) => !selectablePersonIds.includes(String(id))));
      return;
    }
    const next = new Set(selectedPersonIds.map(String));
    selectablePersonIds.forEach((id) => next.add(id));
    setSelectedPersonIds(Array.from(next));
  };

  const toggleSelectPerson = (personId) => {
    const id = String(personId || '');
    if (!id) return;
    const target = rows.find((row) => String(row.id) === id);
    if (target && isOwnProfileRow(target)) return;
    setSelectedPersonIds((prev) => {
      const next = new Set(prev.map(String));
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return Array.from(next);
    });
  };

  const handleBulkRoleApply = async () => {
    if (!bulkRole) {
      alert('Selecione um perfil para aplicar aos usuários selecionados.');
      return;
    }
    if (!selectedPeople.length) return;
    if (!validateRoleAssignment(selectedPeople, bulkRole)) return;
    const profileIds = Array.from(new Set(selectedPeople.map((row) => row.profile_id).filter(Boolean)));
    setBulkApplying(true);
    try {
      await persistProfileRole(profileIds, bulkRole);
      profileIds.forEach((profileId) => patchLocalProfile(profileId, { role: bulkRole }));
      setSelectedPersonIds([]);
      setBulkRole('');
      alert(`Perfil "${bulkRole}" aplicado a ${profileIds.length} usuário(s).`);
    } catch (err) {
      alert(`Erro ao aplicar perfil: ${err.message}`);
    } finally {
      setBulkApplying(false);
    }
  };

  const openProfileEditModal = (row) => {
    if (isOwnProfileRow(row)) {
      alert('Você não pode alterar o seu próprio perfil.');
      return;
    }
    setProfileEditModal({
      row,
      role: String(row.sofera_role || 'Aluno'),
    });
  };

  const handleSaveProfileEdit = async () => {
    if (!profileEditModal) return;
    const { row, role } = profileEditModal;
    if (!role) {
      alert('Selecione um perfil.');
      return;
    }
    if (String(row.sofera_role || '') === role) {
      setProfileEditModal(null);
      return;
    }
    if (!validateRoleAssignment([row], role)) return;
    setProfileEditSaving(true);
    try {
      let profileId = String(row.profile_id || '');
      if (!profileId) {
        // Usuário do Core ainda sem conta na SOFERA: provisiona via espelhamento,
        // criando auth.users + profiles (role padrão 'Aluno'), e depois aplica
        // o perfil escolhido.
        const { data, error } = await supabase.rpc('core_mirror_person', {
          p_central_person_id: String(row.id),
          p_company_id: row.local_company_id || null,
        });
        if (error) throw new Error(error.message);
        profileId = data ? String(data) : '';
        if (!profileId) throw new Error('Não foi possível criar a conta na SOFERA para este usuário.');
      }
      await persistProfileRole([profileId], role);
      patchLocalProfile(profileId, { role });
      setProfileEditModal(null);
    } catch (err) {
      alert(`Erro ao alterar perfil: ${err.message}`);
    } finally {
      setProfileEditSaving(false);
    }
  };

  const counts = cache?.counts || {};
  const index = useMemo(() => buildCoreBaseIndex(cache?.dataset), [cache]);

  // Cards de Pessoas refletem os mesmos status exibidos na coluna STATUS,
  // sempre considerando os filtros aplicados na tela.
  const peopleMetrics = useMemo(() => {
    const byLabel = new Map();
    filteredRows.forEach((row) => {
      const label = row.statusInfo?.label || 'Sem convite';
      byLabel.set(label, (byLabel.get(label) || 0) + 1);
    });
    const base = [
      { label: 'Usuários', value: filteredRows.length, icon: Users, accentClass: 'text-white' },
      { label: 'Ativos', value: byLabel.get('Ativo') || 0, icon: UserCheck, accentClass: 'text-teal-300' },
      { label: 'Pendentes', value: byLabel.get('Pendente') || 0, icon: Clock, accentClass: 'text-amber-300' },
      { label: 'Inativos', value: byLabel.get('Inativo') || 0, icon: UserX, accentClass: 'text-slate-400' },
    ];
    const extras = [
      { key: 'Suspenso', label: 'Suspensos', icon: Ban, accentClass: 'text-rose-300' },
      { key: 'Aceito', label: 'Aceitos', icon: ShieldCheck, accentClass: 'text-sky-300' },
      { key: 'Expirado', label: 'Expirados', icon: Clock, accentClass: 'text-orange-300' },
      { key: 'Cancelado', label: 'Cancelados', icon: Ban, accentClass: 'text-slate-400' },
      { key: 'Recusado', label: 'Recusados', icon: UserX, accentClass: 'text-rose-300' },
      { key: 'Sem convite', label: 'Sem convite', icon: MailX, accentClass: 'text-slate-400' },
    ]
      .filter((item) => (byLabel.get(item.key) || 0) > 0)
      .map((item) => ({
        label: item.label,
        value: byLabel.get(item.key),
        icon: item.icon,
        accentClass: item.accentClass,
      }));
    return [...base, ...extras];
  }, [filteredRows]);

  // Cards por perfil: contam os mesmos perfis exibidos na coluna PERFIL,
  // sempre considerando os filtros aplicados na tela.
  const roleMetrics = useMemo(() => {
    if (activeEntity !== 'people') return [];
    const byRole = new Map();
    filteredRows.forEach((row) => {
      const role = String(row.sofera_role || '').trim() || 'Sem perfil';
      byRole.set(role, (byRole.get(role) || 0) + 1);
    });
    const ordered = [
      ...soferaRoles,
      ...Array.from(byRole.keys()).filter((role) => !soferaRoles.includes(role)),
    ];
    return ordered
      .filter((role) => (byRole.get(role) || 0) > 0)
      .map((role) => ({
        label: role,
        value: byRole.get(role),
        icon: ShieldCheck,
        accentClass: 'text-teal-300',
      }));
  }, [filteredRows, soferaRoles, activeEntity]);

  const metrics = activeEntity === 'people'
    ? peopleMetrics
    : [
      { label: 'Empresas', value: index.companies.length, icon: Building2 },
      { label: 'Pessoas', value: index.people.length, icon: Users },
      { label: 'Vínculos', value: index.company_users.length, icon: Link2 },
      { label: 'Áreas', value: index.company_areas.length, icon: Layers },
      { label: 'Cargos', value: index.company_positions.length, icon: ShieldCheck },
      { label: 'Produtos', value: index.products.length, icon: Package },
    ];

  const productCode = cache?.product_code || 'SOFERA';

  return (
    <div>
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div>
          <h2 className="flex items-center gap-2 text-lg font-black uppercase tracking-wide text-white">
            <Database size={20} className="text-teal-400" />
            Base Única · A2Care Core
          </h2>
          <p className="mt-1 max-w-2xl text-xs text-slate-500">
            Leitura somente consulta da base central. Produto <span className="font-bold text-teal-400">{productCode}</span>.
            A alteração de perfil é aplicada apenas na SOFERA e não é enviada de volta ao Core.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <div className="text-right">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Última sincronização</div>
            <div className="text-sm font-bold text-slate-300">
              {cache?.synced_at ? formatSaoPauloDateTimeLabel(cache.synced_at) : 'Nunca'}
            </div>
          </div>
          <button
            type="button"
            onClick={handleSync}
            disabled={syncing}
            className="inline-flex min-h-[48px] items-center gap-2 rounded-lg bg-teal-500 px-5 text-sm font-bold uppercase tracking-wide text-slate-950 transition hover:bg-teal-400 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {syncing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
            {syncing ? 'Sincronizando' : 'Sincronizar'}
          </button>
        </div>
      </div>

      {error ? (
        <div className="mb-6 flex items-start gap-3 rounded-lg border border-rose-500/30 bg-rose-500/10 px-4 py-3 text-sm text-rose-200">
          <AlertCircle size={18} className="mt-0.5 shrink-0" />
          <div>
            <div className="font-bold">Falha na integração com a Base Única</div>
            <div className="text-rose-200/80">{error}</div>
          </div>
        </div>
      ) : null}

      {loading ? (
        <div className="flex items-center justify-center rounded-xl border border-slate-800 bg-slate-900/50 py-16 text-slate-400">
          <Loader2 size={20} className="mr-3 animate-spin text-teal-400" />
          Carregando Base Única...
        </div>
      ) : !cache ? (
        <CoreEmptyState onSync={handleSync} syncing={syncing} />
      ) : (
        <>
          <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
            {metrics.map((metric) => (
              <CoreMetricCard key={metric.label} {...metric} />
            ))}
          </div>

          {activeEntity === 'people' && roleMetrics.length > 0 ? (
            <div className="mb-6">
              <div className="mb-2 text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                Usuários por perfil
              </div>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
                {roleMetrics.map((metric) => (
                  <CoreMetricCard key={metric.label} {...metric} />
                ))}
              </div>
            </div>
          ) : null}

          <div className="mb-4 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-wrap gap-2">
              {visibleEntities.map((key) => {
                const item = ENTITY_VIEWS[key];
                const Icon = item.icon;
                const isActive = activeEntity === key;
                const total = key === 'productLinks'
                  ? index.company_products.length
                  : (index[key] || []).length;
                return (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setActiveEntity(key)}
                    className={`inline-flex items-center gap-2 rounded-lg border px-3 py-2 text-xs font-bold uppercase tracking-wide transition ${
                      isActive
                        ? 'border-teal-400 bg-teal-500/10 text-teal-300'
                        : 'border-slate-800 bg-slate-900 text-slate-500 hover:border-slate-700 hover:text-slate-300'
                    }`}
                  >
                    <Icon size={14} />
                    {item.label}
                    <span className={`rounded px-1.5 py-0.5 text-[10px] ${isActive ? 'bg-teal-500/20 text-teal-200' : 'bg-slate-800 text-slate-500'}`}>
                      {total}
                    </span>
                  </button>
                );
              })}
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              {SCOPED_ENTITIES.has(activeEntity) ? (
                <label className="flex min-h-[48px] cursor-pointer items-center gap-3 rounded-lg border border-slate-800 bg-slate-950 px-4 transition hover:border-slate-700">
                  <input
                    type="checkbox"
                    checked={onlyScoped}
                    onChange={(event) => setOnlyScoped(event.target.checked)}
                    className="h-4 w-4 accent-teal-500"
                  />
                  <span className="text-xs font-bold uppercase tracking-wide text-slate-400">
                    Apenas vínculo {productCode}
                  </span>
                </label>
              ) : null}
              {activeEntity === 'people' ? (
                <button
                  type="button"
                  onClick={handleExportExcel}
                  disabled={exporting || sortedRows.length === 0}
                  title="Exportar para Excel respeitando os filtros aplicados"
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-emerald-600 px-5 text-xs font-bold uppercase tracking-wide text-white transition hover:bg-emerald-500 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {exporting ? <Loader2 size={16} className="animate-spin" /> : <FileSpreadsheet size={16} />}
                  {exporting ? 'Exportando' : 'Exportar Excel'}
                </button>
              ) : null}
            </div>
          </div>

          {filterConfigs.length > 0 ? (
            <div className="mb-4 rounded-xl border border-slate-800 bg-slate-900/80 p-4">
              <div className="mb-3 flex items-center justify-between gap-3">
                <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Filtros</span>
                {hasActiveFilters ? (
                  <button
                    type="button"
                    onClick={clearFilters}
                    className="inline-flex items-center gap-1 text-[11px] font-bold uppercase tracking-wide text-teal-400 transition hover:text-teal-300"
                  >
                    <X size={12} />
                    Limpar filtros
                  </button>
                ) : null}
              </div>
              <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 ${filterConfigs.length >= 6 ? 'xl:grid-cols-6' : filterConfigs.length >= 5 ? 'xl:grid-cols-5' : 'xl:grid-cols-4'}`}>
                {filterConfigs.map((filter) => (
                  <CoreFilterSelect
                    key={filter.key}
                    label={filter.label}
                    allLabel={filter.allLabel}
                    options={filterOptions[filter.key] || []}
                    value={filters[filter.key] || []}
                    onChange={(next) => setFilters((current) => ({ ...current, [filter.key]: next }))}
                  />
                ))}
              </div>
            </div>
          ) : null}

          {activeEntity === 'people' && selectedPersonIds.length > 0 ? (
            <div className="mb-4 flex flex-col gap-3 rounded-xl border border-teal-500/30 bg-teal-500/10 p-4 sm:flex-row sm:items-end sm:justify-between">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-teal-300">Ações em massa</div>
                <div className="mt-0.5 text-sm font-bold text-white">
                  {selectedPersonIds.length} {selectedPersonIds.length === 1 ? 'usuário selecionado' : 'usuários selecionados'}
                </div>
                <div className="mt-1 text-[11px] text-slate-400">O perfil é alterado apenas na SOFERA.</div>
              </div>
              <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
                <div className="flex flex-col gap-1">
                  <label htmlFor="bulk-role-select" className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">
                    Perfil
                  </label>
                  <select
                    id="bulk-role-select"
                    value={bulkRole}
                    onChange={(event) => setBulkRole(event.target.value)}
                    className="min-h-[48px] min-w-[200px] rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-white outline-none focus:border-teal-500"
                  >
                    <option value="">Selecione um perfil...</option>
                    {soferaRoles.map((role) => (
                      <option key={role} value={role}>{role}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={handleBulkRoleApply}
                  disabled={bulkApplying || !bulkRole}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg bg-teal-500 px-5 text-sm font-bold uppercase tracking-wide text-slate-950 transition hover:bg-teal-400 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {bulkApplying ? <Loader2 size={16} className="animate-spin" /> : <ShieldCheck size={16} />}
                  {bulkApplying ? 'Aplicando' : 'Aplicar'}
                </button>
                <button
                  type="button"
                  onClick={() => { setSelectedPersonIds([]); setBulkRole(''); }}
                  disabled={bulkApplying}
                  className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-lg border border-slate-700 px-4 text-sm font-bold uppercase tracking-wide text-slate-300 transition hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <X size={14} />
                  Limpar seleção
                </button>
              </div>
            </div>
          ) : null}

          <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900 shadow-xl">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-950/80 text-sm font-semibold uppercase text-slate-400">
                  <tr>
                    {activeEntity === 'people' ? (
                      <th className="w-[52px] px-4 py-3">
                        <label className="flex cursor-pointer items-center justify-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded accent-teal-500"
                            checked={allSelectableSelected}
                            ref={(el) => {
                              if (el) el.indeterminate = someSelectableSelected;
                            }}
                            onChange={toggleSelectAllPeople}
                            disabled={selectablePersonIds.length === 0}
                            title={
                              allSelectableSelected
                                ? 'Desmarcar todos os usuários com conta na SOFERA'
                                : 'Selecionar todos os usuários com conta na SOFERA'
                            }
                          />
                        </label>
                      </th>
                    ) : null}
                    {columns.map((column) => (
                      <th
                        key={column.key}
                        className={`whitespace-nowrap px-4 py-3 ${column.kind === 'product-link' ? 'text-center' : ''} ${column.headClass || ''}`}
                      >
                        {column.sortable ? (
                          <div className="flex items-center gap-2">
                            <span>{column.label}</span>
                            <button
                              type="button"
                              onClick={() => toggleSort(column.key)}
                              title={`Classificar por ${column.label} (${sortConfig?.columnKey === column.key && sortConfig.direction === 'desc' ? 'Z para A' : 'A para Z'})`}
                              className={`inline-flex items-center gap-1 rounded border px-2 py-1 text-[10px] font-bold normal-case transition ${
                                sortConfig?.columnKey === column.key
                                  ? 'border-teal-500/60 bg-teal-500/10 text-teal-300 shadow-sm shadow-teal-950/40'
                                  : 'border-slate-700 bg-slate-900/80 text-slate-300 hover:border-slate-500 hover:text-white'
                              }`}
                            >
                              <ArrowUpDown size={12} />
                              {sortConfig?.columnKey === column.key && sortConfig.direction === 'desc' ? 'Z/A' : 'A/Z'}
                            </button>
                          </div>
                        ) : column.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800">
                  {pagedRows.length === 0 ? (
                    <tr>
                      <td colSpan={columns.length + (activeEntity === 'people' ? 1 : 0)} className="px-4 py-10 text-center text-sm text-slate-500">
                        Nenhum registro encontrado.
                      </td>
                    </tr>
                  ) : (
                    pagedRows.map((row) => (
                      <tr
                        key={row.id}
                        onClick={() => activeEntity === 'people' && openProfileEditModal(row)}
                        className={`transition hover:bg-slate-800/40 ${activeEntity === 'people' ? 'cursor-pointer' : ''}`}
                      >
                        {activeEntity === 'people' ? (
                          <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                            <label className={`flex items-center justify-center ${row.has_profile && !isOwnProfileRow(row) ? 'cursor-pointer' : 'cursor-not-allowed'}`}>
                              <input
                                type="checkbox"
                                className="h-4 w-4 rounded accent-teal-500"
                                checked={selectedPersonIdSet.has(String(row.id))}
                                onChange={() => toggleSelectPerson(row.id)}
                                disabled={!row.has_profile || isOwnProfileRow(row)}
                                title={
                                  isOwnProfileRow(row)
                                    ? 'Você não pode alterar o seu próprio perfil'
                                    : row.has_profile
                                      ? 'Selecionar usuário'
                                      : 'Sem conta na SOFERA — perfil Aluno padrão'
                                }
                              />
                            </label>
                          </td>
                        ) : null}
                        {columns.map((column) => (
                          <td
                            key={column.key}
                            onClick={(column.kind === 'profile-select' || column.kind === 'person-status')
                              ? (e) => e.stopPropagation()
                              : undefined}
                            className={`px-4 py-3 text-sm text-slate-300 ${column.kind === 'mono' ? 'font-mono text-xs' : ''} ${column.kind === 'product-link' ? 'text-center' : ''} ${column.cellClass || ''}`}
                          >
                            {column.kind === 'product-link' ? (
                              row.productLinks?.has(column.productId)
                                ? <Check size={16} className="mx-auto text-teal-400" />
                                : null
                            ) : column.kind === 'company-areas' ? (
                              <button
                                type="button"
                                onClick={() => openDetailModal('areas', row)}
                                disabled={!row.areas_count}
                                className="inline-flex items-center gap-1.5 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-xs font-bold text-teal-300 transition hover:bg-teal-500/20 hover:text-teal-200 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <Layers size={13} />
                                {row.areas_count || 0}
                              </button>
                            ) : column.kind === 'company-positions' ? (
                              <button
                                type="button"
                                onClick={() => openDetailModal('positions', row)}
                                disabled={!row.positions_count}
                                className="inline-flex items-center gap-1.5 rounded-full border border-teal-500/30 bg-teal-500/10 px-3 py-1 text-xs font-bold text-teal-300 transition hover:bg-teal-500/20 hover:text-teal-200 disabled:cursor-not-allowed disabled:opacity-40"
                              >
                                <ShieldCheck size={13} />
                                {row.positions_count || 0}
                              </button>
                            ) : column.kind === 'profile-select' ? (
                              row.has_profile ? (
                                <select
                                  value={row.sofera_role || ''}
                                  onChange={(event) => handleRoleChange(row, event.target.value)}
                                  disabled={!!savingRoleByPerson[row.id] || isOwnProfileRow(row)}
                                  title={isOwnProfileRow(row) ? 'Você não pode alterar o seu próprio perfil' : 'Alterar perfil na SOFERA'}
                                  className="min-h-[40px] w-full min-w-[110px] rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-white outline-none transition focus:border-teal-500 disabled:cursor-not-allowed disabled:opacity-60"
                                >
                                  <option value="">Selecione...</option>
                                  {roleOptionsFor(row).map((role) => (
                                    <option key={role} value={role}>{role}</option>
                                  ))}
                                </select>
                              ) : (
                                <div
                                  className="flex min-h-[40px] w-full min-w-[110px] items-center justify-between gap-2 rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-white"
                                  title="Perfil padrão de todo usuário vindo do Core. Pode ser alterado após a criação da conta na SOFERA."
                                >
                                  <span>Aluno</span>
                                  <span className="text-[10px] font-bold uppercase tracking-wide text-slate-500">padrão</span>
                                </div>
                              )
                            ) : column.kind === 'status' ? (
                              <CoreStatusBadge status={row[column.key]} />
                            ) : column.kind === 'person-status' ? (
                              <PersonStatusBadge
                                statusInfo={row.statusInfo}
                                onResend={() => handleResendInvite(row)}
                                resending={!!resendState[row.id]}
                              />
                            ) : column.truncate ? (
                              <div
                                className="max-w-[240px] truncate"
                                title={getDisplayValue(row, column)}
                              >
                                {getDisplayValue(row, column)}
                              </div>
                            ) : (
                              getDisplayValue(row, column)
                            )}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
            <div className="px-4 pb-4">
              <DataTablePagination
                totalItems={sortedRows.length}
                itemsPerPage={itemsPerPage}
                currentPage={page}
                onItemsPerPageChange={setItemsPerPage}
                onPageChange={setPage}
                activePageClass="bg-teal-500 text-slate-950"
                selectFocusClass="focus:border-teal-500"
              />
            </div>
          </div>
        </>
      )}

      {detailModal ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div>
                <h3 className="text-base font-black uppercase tracking-wide text-white">
                  {detailModal.type === 'areas' ? 'Áreas' : 'Cargos'} da Empresa
                </h3>
                <p className="mt-0.5 text-xs text-slate-400">{detailModal.companyName}</p>
              </div>
              <button
                type="button"
                onClick={() => setDetailModal(null)}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-4">
              {detailModal.items.length === 0 ? (
                <p className="py-8 text-center text-sm text-slate-500">
                  Nenhum registro encontrado para esta empresa.
                </p>
              ) : (
                <ul className="divide-y divide-slate-800">
                  {detailModal.items.map((item, idx) => (
                    <li key={idx} className="flex items-center justify-between py-2.5">
                      <span className="text-sm font-medium text-slate-200">{item.name}</span>
                      <CoreStatusBadge status={item.status} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={() => setDetailModal(null)}
                className="rounded-lg bg-slate-800 px-5 py-2 text-sm font-bold text-white transition hover:bg-slate-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {profileEditModal ? (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-slate-950/80 p-4 backdrop-blur-sm">
          <div className="w-full max-w-lg rounded-xl border border-slate-700 bg-slate-900 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-800 px-6 py-4">
              <div>
                <h3 className="text-base font-black uppercase tracking-wide text-white">
                  Editar Perfil na SOFERA
                </h3>
                <p className="mt-0.5 text-xs text-slate-400">
                  {profileEditModal.row.full_name || profileEditModal.row.email || 'Usuário do Core'}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setProfileEditModal(null)}
                className="rounded-lg p-1.5 text-slate-500 transition hover:bg-slate-800 hover:text-white"
              >
                <X size={18} />
              </button>
            </div>
            <div className="max-h-[60vh] overflow-y-auto px-6 py-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Nome</div>
                  <div className="mt-1 text-sm font-semibold text-white">{profileEditModal.row.full_name || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">E-mail</div>
                  <div className="mt-1 text-sm text-slate-200">{profileEditModal.row.email || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">CPF</div>
                  <div className="mt-1 text-sm text-slate-200">{profileEditModal.row.cpf || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Empresa</div>
                  <div className="mt-1 text-sm text-slate-200">{profileEditModal.row.company_name || '—'}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">Cargo</div>
                  <div className="mt-1 text-sm text-slate-200">{profileEditModal.row.positions || '—'}</div>
                </div>
              </div>

              <div className="mt-6">
                <label htmlFor="profile-edit-role" className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-500">
                  Perfil na SOFERA
                </label>
                <select
                  id="profile-edit-role"
                  value={profileEditModal.role}
                  onChange={(event) => setProfileEditModal((prev) => prev ? { ...prev, role: event.target.value } : prev)}
                  className="mt-2 min-h-[48px] w-full rounded-lg border border-slate-800 bg-slate-950 px-3 text-sm text-white outline-none transition focus:border-teal-500"
                >
                  {roleOptionsFor(profileEditModal.row).map((role) => (
                    <option key={role} value={role}>{role}</option>
                  ))}
                </select>
                {!profileEditModal.row.has_profile ? (
                  <p className="mt-2 text-[11px] text-amber-300/80">
                    Este usuário ainda não tem conta na SOFERA. Ao salvar, a conta será criada (espelhamento do Core) com o perfil selecionado.
                  </p>
                ) : null}
                {PARTNER_ROLES.has(profileEditModal.role) && !profileEditModal.row.sas_partner_id ? (
                  <p className="mt-2 text-[11px] text-rose-300/80">
                    O perfil "{profileEditModal.role}" exige um Parceiro SAS vinculado.
                  </p>
                ) : null}
              </div>
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-800 px-6 py-4">
              <button
                type="button"
                onClick={() => setProfileEditModal(null)}
                disabled={profileEditSaving}
                className="rounded-lg bg-slate-800 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-slate-700 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleSaveProfileEdit}
                disabled={profileEditSaving}
                className="inline-flex items-center gap-2 rounded-lg bg-teal-500 px-5 py-2.5 text-sm font-bold text-slate-950 transition hover:bg-teal-400 disabled:opacity-60"
              >
                {profileEditSaving ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                {profileEditSaving ? 'Salvando...' : 'Salvar perfil'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default CoreBaseTab;
